import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'motion/react';

export type TipoAviso = 'neutro' | 'sucesso' | 'aviso' | 'erro';

interface AvisoProps {
  aberto: boolean;
  texto: string;
  tipo?: TipoAviso;
  aoFechar: () => void;
  /** Tempo na tela (padrão 3 s). */
  duracao?: number;
}

const FUNDO: Record<TipoAviso, string> = {
  neutro: 'bg-foreground-900/92 text-background-50',
  sucesso: 'bg-success-600/96 text-white',
  aviso: 'bg-warning-500/98 text-warning-950',
  erro: 'bg-danger-600/96 text-white',
};

// Aviso (toast) discreto: entra deslizando de baixo e sai sozinho em 3 s.
// Sem botão, sem travar o toque no resto da tela.
export default function Aviso({
  aberto,
  texto,
  tipo = 'neutro',
  aoFechar,
  duracao = 3000,
}: AvisoProps) {
  useEffect(() => {
    if (!aberto) return;
    const timer = window.setTimeout(aoFechar, duracao);
    return () => window.clearTimeout(timer);
  }, [aberto, duracao, aoFechar]);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <div className="pointer-events-none fixed inset-x-0 bottom-0 z-[9999] flex justify-center px-4 pb-[max(1.25rem,env(safe-area-inset-bottom))]">
      <AnimatePresence>
        {aberto && (
          <motion.div
            key="aviso"
            role="status"
            aria-live="polite"
            className={`pointer-events-none max-w-[calc(100vw-2rem)] rounded-full px-5 py-2.5 text-center text-[15px] font-medium leading-snug ${FUNDO[tipo]}`}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 24 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
          >
            {texto}
          </motion.div>
        )}
      </AnimatePresence>
    </div>,
    document.body,
  );
}